import { useEffect, useMemo, useRef, useState } from 'react';
import { JobMonitor } from './components/JobMonitor';
import { MomentsSection } from './components/MomentsSection';
import { NumberedHeading } from './components/NumberedHeading';
import { ProgressBar } from './components/ProgressBar';
import { RecentPortraits } from './components/RecentPortraits';
import { StepBar } from './components/StepBar';
import { StyleSelector } from './components/StyleSelector';
import { UploadForm, type UploadFormData } from './components/UploadForm';
import { useGenerateFlow } from './hooks/useGenerateFlow';
import { useWakeLock } from './hooks/useWakeLock';
import type { StyleSample, WidgetMountData } from './types';
import { getInputConfig } from './utils/inputConfigs';
import { shopifyImg } from './utils/img';
import { saveRecentPortrait } from './utils/recentPortraits';

interface Props {
  data: WidgetMountData;
  view: 'upload' | 'preview';
}

/**
 * WidgetApp — the landing-page upload flow only.
 *
 * Choose style → upload photos → generate → poll. The moment the job is done
 * we navigate to the standalone, shareable preview page
 * (`{previewPage}?id=<jobId>`) instead of rendering the preview inline. The
 * preview page (PreviewApp, mounted on #bf-preview-root) owns versions /
 * format / checkout / retry, so a portrait is always reachable by its URL.
 */
export function WidgetApp({ data }: Props) {
  // Merged multi-collection product (ui_config.looks) — "Choose Your Look"
  // picked once, up front; everything below then reads from that look
  // instead of the top-level ui_config fields. Absent for every other
  // product, which behaves exactly as before.
  const looks = data.uiConfig.looks ?? [];
  const hasLooks = looks.length > 0;
  const [selectedLookId, setSelectedLookId] = useState<string | null>(null);
  const activeLook = hasLooks ? looks.find((l) => l.id === selectedLookId) ?? null : null;

  const hideClothingPreference = hasLooks
    ? activeLook?.hide_clothing_preference
    : data.uiConfig.hide_clothing_preference;
  const captionColor = hasLooks ? activeLook?.caption_color : data.uiConfig.caption_color;

  const config = useMemo(() => {
    const base = getInputConfig(data.inputSetCode);
    // Per-product override (ui_config.hide_clothing_preference, or the
    // active look's own copy of it for a merged product) — some
    // familypet-caption-45/-50 products/looks have no clothing-variant
    // prompts at all (only the plain "{style_id}" key, no
    // "__artist_choice"/"__keep_original"), so the picker has nothing
    // meaningful to choose between. GenerateHandler.php's
    // resolveStylePrompt() already falls back to the plain key when no
    // `clothing` is sent, so hiding the step client-side (and simply never
    // sending `clothing`) is the whole fix — no backend change needed.
    if (hideClothingPreference) {
      return { ...base, showClothing: false, clothingOptions: undefined, clothingDefaultId: undefined };
    }
    return base;
  }, [data.inputSetCode, hideClothingPreference]);

  // While a merged product's look hasn't been picked yet, there are no
  // style samples to show — UploadStep doesn't render until selectedLookId
  // is set (see below).
  const styleSamples = hasLooks ? (activeLook?.style_samples ?? []) : (data.uiConfig.style_samples ?? []);

  const [selectedStyleId, setSelectedStyleId] = useState<string | null>(null);
  const { state, start, reset } = useGenerateFlow();
  const rootRef = useRef<HTMLDivElement>(null);

  // On completion, redirect to the standalone preview page. The generated job
  // was indexed under its session by the bridge, so the preview page rebuilds
  // everything from just the job_id.
  useEffect(() => {
    if (state.status !== 'done' || !state.result) return;
    // Remember this portrait in the browser so the "Recent Portraits" strip
    // shows it on the next visit to the product page.
    const style = styleSamples.find((s) => s.id === selectedStyleId);
    saveRecentPortrait({
      jobId: state.result.jobId,
      previewUrl: state.result.previewUrl,
      styleName: style?.name ?? style?.badge,
      createdAt: new Date().toISOString(),
    });
    const page = data.previewPage || '/pages/preview';
    window.location.assign(`${page}?id=${encodeURIComponent(state.result.jobId)}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.status, state.result, data.previewPage]);

  // When generation starts, scroll up so the step bar + processing view are
  // in view (the Generate button sits well below the fold).
  useEffect(() => {
    if (state.status === 'uploading') {
      rootRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [state.status]);

  const handleSubmit = (form: UploadFormData) => {
    const style = styleSamples.find((s) => s.id === selectedStyleId);
    if (!style) return;
    // Merged product: compound "{look_id}__{combo_id}" so this look's
    // prompts never collide with the other looks' under the same
    // product_id in ai_prompts (see types.ts Look doc-comment).
    const styleId = hasLooks && activeLook ? `${activeLook.id}__${style.id}` : style.id;
    start({
      files: form.files,
      productId: data.productId,
      styleId,
      styleName: style.name ?? style.badge,
      portraitMode: config.portraitMode,
      manaquinUrl: resolveManaquin(style, form.charCount),
      charCount: form.charCount,
      inputSetCode: data.inputSetCode,
      humanName: form.humanName,
      petName: form.petName,
      petYears: form.petYears,
      petQuote: form.petQuote,
      caption: form.caption,
      captionColor,
      clothing: form.clothing,
    });
  };

  const generating = state.status === 'uploading' || state.status === 'generating';
  const redirecting = state.status === 'done';
  const failed = state.status === 'failed';
  const processing = generating || redirecting;

  // Keep the phone screen awake while a portrait is generating (~80s).
  useWakeLock(processing);
  // Step 2 becomes active as soon as a style is picked (buyer moves to upload).
  const activeStep: 1 | 2 | 3 = redirecting ? 3 : selectedStyleId ? 2 : 1;

  // Engagement content shown only while a portrait is being generated:
  // a 2-up gallery of the chosen style's examples + the moments / testimonials.
  const selectedStyle = styleSamples.find((s) => s.id === selectedStyleId) ?? null;
  // Two example images of the chosen style. Prefer its own gallery; if it has
  // fewer than 2 valid images, top up from its manaquin gallery and the other
  // styles' galleries (deduped) so the 2-up grid is never half-empty.
  // Some metafield entries join multiple URLs into one comma-separated string
  // (e.g. gallery: ["urlA,urlB"]) — split them out so each image is separate.
  const cleanImgs = (arr?: string[]) =>
    (arr ?? [])
      .flatMap((g) => (typeof g === 'string' ? g.split(',') : []))
      .map((s) => s.trim())
      .filter((g) => g.length > 0 && !g.includes('<<'));
  // gallery convention: "img1,img2,img3,img4" — while generating show img3+img4 (index 2,3).
  const styleImgs = cleanImgs(selectedStyle?.gallery);
  let genGallery = styleImgs.slice(2, 4);
  if (genGallery.length === 0) {
    // Fallback (style has <4 images): any available examples.
    let pool = styleImgs
      .concat(cleanImgs(selectedStyle?.manaquin_gallery))
      .concat(styleSamples.flatMap((s) => cleanImgs(s.gallery)));
    pool = Array.from(new Set(pool));
    genGallery = pool.slice(0, 2);
  }
  const moments = data.uiConfig.moments ?? [];

  // Merged product, no look picked yet — show only the look-picker (not the
  // numbered 1/2/3 StepBar; picking a look is a step before that sequence
  // starts). Reuses StyleSelector's card grid by shaping each look like a
  // StyleSample — same visuals, no new component needed.
  if (hasLooks && !selectedLookId) {
    const lookSamples: StyleSample[] = looks.map((l) => ({
      id: l.id,
      name: l.name,
      badge: l.name,
      image: l.image,
      gallery: l.image ? [l.image] : undefined,
    }));
    return (
      <div ref={rootRef} className="w-full max-w-[780px] mx-auto px-4 sm:px-6 py-6 flex flex-col gap-8">
        <section className="flex flex-col gap-4">
          <NumberedHeading n={1}>Choose Your Look</NumberedHeading>
          <StyleSelector samples={lookSamples} selectedId={selectedLookId} onSelect={setSelectedLookId} />
        </section>
      </div>
    );
  }

  return (
    <div ref={rootRef} className="w-full max-w-[780px] mx-auto px-4 sm:px-6 py-6 flex flex-col gap-8">
      <StepBar active={activeStep} />

      {hasLooks && activeLook && (
        <p className="text-xs text-warm-brown/70 m-0">
          Look: <strong className="text-warm-brown">{activeLook.name}</strong>{' '}
          <button
            type="button"
            onClick={() => { setSelectedLookId(null); setSelectedStyleId(null); }}
            className="text-teal underline font-semibold"
          >
            Change
          </button>
        </p>
      )}

      {!processing && !failed && (
        <>
          <UploadStep
            styleSamples={styleSamples}
            selectedStyleId={selectedStyleId}
            onSelectStyle={setSelectedStyleId}
            config={config}
            onSubmit={handleSubmit}
            error={null}
            petQuotes={data.uiConfig.pet_quotes ?? []}
          />

          {/* Recent Portraits — below the Generate button (woo order). */}
          <RecentPortraits previewPage={data.previewPage || '/pages/preview'} />
        </>
      )}

      {failed && (
        <section className="py-10 flex flex-col items-center text-center gap-5">
          <span
            aria-hidden="true"
            style={{ width: 56, height: 56, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          </span>
          <p className="text-sm text-warm-brown/80 max-w-[420px] m-0 leading-relaxed">
            {state.error || 'Something went wrong. Please try again.'}
          </p>
          <button
            type="button"
            onClick={reset}
            className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-teal text-white text-sm font-bold shadow-soft hover:bg-teal-dark transition-colors"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M1 4v6h6M23 20v-6h-6" /><path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4-4.64 4.36A9 9 0 0 1 3.51 15" /></svg>
            Try Again
          </button>
        </section>
      )}

      {processing && (
        <section className="py-8 flex flex-col gap-10 items-center">
          <div className="flex flex-col gap-6 items-center">
            <h2 className="font-sans text-xl font-semibold text-warm-brown text-center m-0">
              {redirecting ? 'Your portrait is ready!' : 'Painting your portrait…'}
            </h2>
            <ProgressBar done={redirecting} />
            {state.status === 'uploading' && (
              <p className="text-xs text-warm-brown/70">Uploading your photos…</p>
            )}
            {redirecting && (
              <p className="text-sm text-warm-brown/80">Taking you to your portrait…</p>
            )}
          </div>

          {/* Gallery — 2 examples of the chosen style, 2 columns (only while generating).
              Inline grid + aspect-ratio so it survives any Tailwind purge/cache. */}
          {genGallery.length > 0 && (
            <div
              className="w-full"
              style={{ maxWidth: 520, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}
            >
              {genGallery.map((src, i) => (
                <div
                  key={i}
                  className="rounded-[14px] overflow-hidden bg-cream2 border border-soft-border shadow-soft"
                  style={{ aspectRatio: '3 / 4' }}
                >
                  <img src={shopifyImg(src, 500)} alt="" loading="lazy" decoding="async" className="w-full h-full" style={{ objectFit: 'cover' }} />
                </div>
              ))}
            </div>
          )}

          {/* Moments / testimonials — only while generating. */}
          <MomentsSection moments={moments} />
        </section>
      )}

      <JobMonitor />
    </div>
  );
}

// ─── Upload step ────────────────────────────────────────────────────────────

interface UploadStepProps {
  styleSamples: StyleSample[];
  selectedStyleId: string | null;
  onSelectStyle: (id: string) => void;
  config: ReturnType<typeof getInputConfig>;
  onSubmit: (data: UploadFormData) => void;
  error: string | null;
  petQuotes: string[];
}

function UploadStep({
  styleSamples,
  selectedStyleId,
  onSelectStyle,
  config,
  onSubmit,
  error,
  petQuotes,
}: UploadStepProps) {
  return (
    <>
      <section className="flex flex-col gap-4">
        <NumberedHeading
          n={1}
          subtitle={config.portraitMode === 'smallfamilypet-caption' ? '(Customize group size in Step 2.)' : undefined}
        >
          {config.dynamicPersonPetGroups ? 'Choose Number Of Persons & Pets' : 'Choose a style'}
        </NumberedHeading>
        <StyleSelector
          samples={styleSamples}
          selectedId={selectedStyleId}
          onSelect={onSelectStyle}
        />
      </section>

      {/* Steps 2 (+3 for pet modes) + Generate button live inside the form. */}
      <UploadForm
        config={config}
        styleId={selectedStyleId}
        styleSelected={!!selectedStyleId}
        onSubmit={onSubmit}
        petQuotes={petQuotes}
      />

      {error && (
        <div className="text-center bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-700">
          <p className="font-medium mb-1">We hit a snag</p>
          <p>{error}</p>
        </div>
      )}
    </>
  );
}

export function resolveManaquin(style: StyleSample, charCount: number): string {
  const gallery = style.manaquin_gallery ?? [];
  if (gallery.length === 0) return '';
  const idx = Math.min(Math.max(0, charCount - 1), gallery.length - 1);
  return gallery[idx] ?? '';
}
